import { Box, Button, Typography, Avatar, Stack } from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import PhoneDisabledIcon from "@mui/icons-material/PhoneDisabled";
import { formatUSPhone } from "../../../../utils";
import { colors, alphaVar } from "../../../ui/colors";
import { useContactCenterTheme } from "../../theming/ContactCenterThemeContext";

export interface CallerInfo {
  displayName?: string;
  phoneNumber?: string;
  dialCode?: string;
  callerType?: string;
  callerLabel?: string;
  receivingCompanyName?: string;
}

interface Props {
  callerInfo: CallerInfo;
  onAnswer: () => void;
  onReject: () => void;
  answerLabel?: string;
}

export const IncomingCallNotification = ({ callerInfo, onAnswer, onReject, answerLabel = "Answer" }: Props) => {
  const { theme } = useContactCenterTheme();
  const { displayName, dialCode, phoneNumber, callerType, receivingCompanyName } = callerInfo;

  const callerInitials = displayName?.at(0)?.toUpperCase() || "?";
  const callerPhoneDisplay = phoneNumber
    ? dialCode
      ? `${dialCode} ${formatUSPhone(phoneNumber)}`
      : phoneNumber
    : null;
  const callerLabel = callerType === "client" ? "Client" : callerType === "workforce" ? "Workforce" : null;

  return (
    <Box sx={{ textAlign: "center", mb: 1.5 }}>
      <Avatar
        sx={{
          width: 48,
          height: 48,
          backgroundColor: theme.shell.accent,
          color: "#fff",
          fontSize: "1.2rem",
          fontWeight: 700,
          margin: "0 auto",
          boxShadow: `0 2px 8px ${alphaVar(theme.shell.accent, 0.3)}`,
        }}
      >
        {callerInitials}
      </Avatar>

      <Typography
        sx={{
          fontSize: "1rem",
          fontWeight: 700,
          textAlign: "center",
          color: theme.shell.textPrimary,
          mb: 0.25,
          mt: 1,
        }}
      >
        {displayName || "Unknown"}
      </Typography>

      {callerPhoneDisplay && callerType !== "workforce" && (
        <Typography sx={{ fontSize: "0.75rem", color: theme.shell.textSecondary, textAlign: "center", mt: 0.25 }}>
          {callerPhoneDisplay}
        </Typography>
      )}

      {callerPhoneDisplay && callerType === "workforce" && (
        <Typography
          sx={{
            fontSize: "0.8rem",
            color: theme.shell.accent,
            textAlign: "center",
            mt: 0.35,
            fontWeight: 600,
          }}
        >
          {callerPhoneDisplay}
        </Typography>
      )}

      {callerLabel && <Typography sx={{ fontSize: "0.75rem", color: theme.shell.textSecondary, textAlign: "center" }}>{callerLabel}</Typography>}

      {receivingCompanyName && (
        <Typography
          sx={{
            fontSize: "0.75rem",
            color: theme.shell.textSecondary,
            textAlign: "center",
            mt: 0.25,
            fontStyle: "italic"
          }}
        >
          For: {receivingCompanyName}
        </Typography>
      )}

      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        <Button
          fullWidth
          onClick={onReject}
          startIcon={<PhoneDisabledIcon sx={{ fontSize: "0.95rem" }} />}
          sx={{
            color: colors.danger.base,
            fontWeight: 600,
            fontSize: "0.85rem",
            padding: "8px 12px",
            textTransform: "none",
            "&:hover": {
              backgroundColor: alphaVar(colors.danger.base, 0.05),
              color: colors.danger[700],
            },
          }}
        >
          Decline
        </Button>

        <Button
          fullWidth
          variant="contained"
          onClick={onAnswer}
          startIcon={<PhoneIcon sx={{ fontSize: "0.95rem" }} />}
          sx={{
            background: `linear-gradient(135deg, ${colors.success.base}, ${colors.success[600]})`,
            color: "white",
            fontWeight: 600,
            fontSize: "0.85rem",
            padding: "8px 12px",
            textTransform: "none",
            boxShadow: `0 2px 8px ${alphaVar(colors.success.base, 0.25)}`,
            borderRadius: 1,
            transition: "all 0.2s ease",
            "&:hover": {
              boxShadow: `0 4px 12px ${alphaVar(colors.success.base, 0.3)}`,
              transform: "scale(1.02)",
            },
            "&:active": {
              transform: "scale(0.98)",
            },
          }}
        >
          {answerLabel}
        </Button>
      </Stack>
    </Box>
  );
};
